import { ColorContext } from 'App';
import AudioPreview from 'components/UI/AudioPreview';
import TextToggle from 'components/UI/TextToggle';
import { useContext, useEffect, useState } from 'react';
import { Button, Card, Col, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { IoInformationCircleOutline } from 'react-icons/io5';
import { useGetAllAvailableDeviceTypesQuery } from 'services/devices/devices.api';
import { displayStatusOptions } from 'utils/iconUtils';
import { GetDeviceTypeString } from 'utils/stringUtils';

export function VerifyToggleFlashingLights(dataObj) {
  if (!dataObj) return false;
  return !(dataObj?.status === '' || dataObj?.comment === '');
}

export default function ToggleFlashingLights({ onUpdate, deviceList, dataObj, group }) {
  const theme = useContext(ColorContext);
  const isOn = ![0, 7, 8].includes(dataObj?.status);
  const { data: deviceTypes = {} } = useGetAllAvailableDeviceTypesQuery();
  const [intervalEnabled, setIntervalEnabled] = useState(!!dataObj?.interval);

  useEffect(() => {
    if (dataObj?.status === undefined || dataObj?.status === null) {
      onUpdate({ status: 0 });
    }

    if (group) {
      const firstDeviceOfGroupType = deviceList.find((device) => device.device_type === group.type);
      if (firstDeviceOfGroupType) {
        onUpdate({
          selectedDevice: {
            ...firstDeviceOfGroupType,
            data: [firstDeviceOfGroupType.data[0]],
          },
        });
      }
    }
  }, [group]);

  const resolvedType =
    dataObj?.selectedDevice?.device_type || dataObj?.deviceType || group?.type || '';

  const normalizedType = String(resolvedType).toLowerCase();

  const isSirenType =
    normalizedType.includes('siren/giant voice') ||
    normalizedType.includes('siren') ||
    normalizedType.includes('giant voice');

  const isManualRoadClosure = normalizedType.includes('manual road closure');
  const isFlashing =
    normalizedType.includes('flashing lights') || normalizedType.includes('flashing_light');

  const getStatusOptions = () => {
    if (isManualRoadClosure) return ['Open', 'Closed'];
    if (isFlashing) return ['Idle', 'Alerting'];
    if (isSirenType) return ['Idle', 'Alerting'];

    const shared = displayStatusOptions({
      ...dataObj,
      deviceType: resolvedType,
      selectedDevice:
        dataObj?.selectedDevice || (resolvedType ? { device_type: resolvedType } : undefined),
    });

    return shared && shared.length ? shared : ['Off', 'On'];
  };

  const statusOptions = getStatusOptions();

  const sirenSelector = () => {
    if (!dataObj?.selectedDevice) return null;

    const types =
      deviceTypes?.[dataObj.selectedDevice.system_type]?.[dataObj.selectedDevice.device_type]
        ?.sirenTypes || [];

    if (!types.length) return null;

    return (
      <Card className="bg-base">
        <Card.Body className="p-3">
          <Row className="g-3 align-items-center bg-base">
            <Col xs={12} md={4}>
              <Form.Label className="m-0 fw-semibold">Siren Tone</Form.Label>
            </Col>
            <Col xs={12} md={8}>
              <Form.Select
                className="mb-2"
                value={dataObj.status}
                onChange={(e) => {
                  const tone = types.find((t) => t.relay_id == e.target.value);
                  onUpdate({ status: tone?.relay_id });
                }}
              >
                {types.map((tone) => (
                  <option key={tone.name} value={tone.relay_id}>
                    {tone.displayName}
                  </option>
                ))}
              </Form.Select>

              <AudioPreview
                type="siren"
                tone={types.find((t) => t.relay_id == dataObj?.status)}
                roadStatus={dataObj.selectedDevice.status}
                setRoadStatus={(value) => onUpdate({ status: value })}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>
    );
  };

  return (
    <Card className="rounded-2 bg-primary">
      <Card.Body className="d-flex flex-column gap-3">
        {/* Device Alias (hidden for groups) */}
        {!group && (
          <Row className="align-items-center">
            <Col xs={12} md={4} className="fw-semibold">
              Device Alias
            </Col>
            <Col xs={12} md={8}>
              <Form.Select
                className="bg-base border-0"
                value={dataObj.node_id || ''}
                onChange={(e) => {
                  const nodeId = e.target.value;
                  const device = deviceList.find((d) => d.node_id.toString() === nodeId);
                  if (device) {
                    onUpdate({
                      node_id: nodeId,
                      selectedDevice: { ...device, data: [device.data[0]] },
                    });
                  } else {
                    onUpdate({ node_id: '', selectedDevice: null });
                  }
                }}
              >
                <option value="">-Select-</option>
                {deviceList?.map((device) => {
                  if (GetDeviceTypeString(device) === dataObj.deviceType) {
                    const label =
                      (device.device_type !== 'Manual Road Closure' ? device.static_id + ' ' : '') +
                      device.alias;
                    return (
                      <option key={device.node_id} value={device.node_id}>
                        {label}
                      </option>
                    );
                  }
                  return null;
                })}
              </Form.Select>
            </Col>
          </Row>
        )}

        {/* Status + Actions */}
        <Row className="align-items-center">
          <Col xs={12} md={4} className="fw-semibold">
            Status
          </Col>
          <Col xs={12} md={8} className="d-flex flex-wrap gap-2">
            <TextToggle
              options={statusOptions}
              value={isOn}
              setValue={(value) => {
                if (isSirenType) {
                  onUpdate({ status: value ? 2 : 0 });
                } else {
                  onUpdate({ status: value ? 1 : 0 });
                }
              }}
              pill
              groupStyle={{
                border: `2px solid ${isOn ? '#C73B1D' : '#01b574'}`,
                borderRadius: '999px',
                background: 'transparent',
              }}
              activeVariant={isOn ? 'danger' : 'success'}
              inactiveVariant="outline-secondary"
              buttonClassName="text-light fw-semibold"
            />

            {isSirenType && (
              <>
                <Button
                  size="sm"
                  variant={dataObj?.status === 7 ? 'warning' : 'outline-secondary'}
                  className="fw-semibold"
                  onClick={() => onUpdate({ status: dataObj?.status === 7 ? 0 : 7 })}
                >
                  Cancel Audio
                </Button>

                <Button
                  size="sm"
                  variant={dataObj?.status === 8 ? 'danger' : 'outline-secondary'}
                  className="fw-semibold"
                  onClick={() => onUpdate({ status: dataObj?.status === 8 ? 0 : 8 })}
                >
                  Power Cycle Device
                </Button>
              </>
            )}
          </Col>
        </Row>

        {/* Siren Selector */}
        {isSirenType &&
          (dataObj?.status === 1 || dataObj?.status === 2 || dataObj?.status === 6) &&
          sirenSelector()}

        {/* Repeat controls */}
        {isSirenType &&
          (dataObj?.status === 1 || dataObj?.status === 2 || dataObj?.status === 6) && (
            <Row className="g-3 align-items-center">
              <Col xs={12} md={4} className="fw-semibold">
                Repeat
              </Col>

              <Col xs={12} md={8} className="d-flex flex-wrap align-items-center gap-3">
                <TextToggle
                  options={['Off', 'On']}
                  value={intervalEnabled}
                  setValue={(enabled) => {
                    setIntervalEnabled(enabled);

                    if (enabled) {
                      const safeInterval = Math.min(
                        7200,
                        Math.max(30, Number(dataObj?.interval || 30)),
                      );
                      onUpdate({ interval: safeInterval });
                    } else {
                      onUpdate({ interval: null });
                    }
                  }}
                  pill
                  groupStyle={{
                    borderRadius: '999px',
                    background: theme.base,
                    overflow: 'hidden',
                  }}
                  activeVariant="primary"
                  inactiveVariant="outline-light"
                  buttonClassName="text-light fw-semibold"
                />

                <div className="d-flex align-items-baseline gap-2">
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip
                        style={{
                          maxWidth: 400,
                          '--bs-tooltip-bg': '#ffffff',
                          '--bs-tooltip-color': '#111111',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
                        }}
                      >
                        This controls how often the event should repeat, in seconds. Valid range:
                        30–7200.
                      </Tooltip>
                    }
                  >
                    <div className="d-flex align-items-center" style={{ color: '#ffffff' }}>
                      <IoInformationCircleOutline size={22} />
                    </div>
                  </OverlayTrigger>

                  <Form.Control
                    type="number"
                    min={30}
                    max={7200}
                    disabled={!intervalEnabled}
                    value={dataObj?.interval ?? 30}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const num = Number(raw);
                      if (raw === '') return onUpdate({ interval: '' });
                      if (!Number.isNaN(num)) onUpdate({ interval: num });
                    }}
                    onBlur={() => {
                      if (!intervalEnabled) return;
                      const clamped = Math.min(7200, Math.max(30, Number(dataObj?.interval || 30)));
                      onUpdate({ interval: clamped });
                    }}
                  />
                </div>
              </Col>
            </Row>
          )}

        {/* Comments */}
        <Row className="g-3">
          <Col xs={12} md={4} className="fw-semibold">
            Comments
          </Col>
          <Col xs={12} md={8}>
            <Form.Control
              as="textarea"
              rows={4}
              value={dataObj.comment || ''}
              onChange={(e) => onUpdate({ comment: e.target.value })}
              placeholder="(Optional)"
              className="bg-base border-0"
            />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}
